import { AppWindow, FileText, HardDrive, PanelRight, Presentation, Table } from "lucide-react";

import { useArtifactStore, type Artifact } from "@/store/artifactStore";

interface ArtifactBannerProps {
  readonly artifact: Artifact;
  readonly sublabel?: string;
}

function iconFor(artifact: Artifact) {
  if (artifact.kind === "app") return AppWindow;
  if (artifact.kind === "google") {
    switch (artifact.embed.kind) {
      case "sheets":
        return Table;
      case "slides":
        return Presentation;
      case "drive":
        return HardDrive;
      default:
        return FileText;
    }
  }
  return FileText;
}

/**
 * In-chat banner for a document the agent produced or referenced. Clicking
 * opens it in the side panel (ArtifactPanel); the panel collapses any time
 * and this banner stays in the transcript to reopen it.
 */
export function ArtifactBanner({ artifact, sublabel }: ArtifactBannerProps) {
  const openArtifact = useArtifactStore((s) => s.openArtifact);
  const Icon = iconFor(artifact);

  return (
    <button
      type="button"
      onClick={() => openArtifact(artifact)}
      className="not-prose mt-3 flex w-full items-center gap-3 rounded-xl border border-black/10 bg-white px-3.5 py-3 text-left shadow-sm transition-all duration-200 hover:-translate-y-px hover:shadow-md dark:border-white/15 dark:bg-white/5"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-home-butter">
        <Icon className="h-4.5 w-4.5 text-home-ink" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-black dark:text-white">
          {artifact.label}
        </span>
        <span className="block truncate text-xs text-black/50 dark:text-white/50">
          {sublabel ?? "Click to open"}
        </span>
      </span>
      <PanelRight className="h-4 w-4 shrink-0 text-black/40 dark:text-white/40" />
    </button>
  );
}
